import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Boxes,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Sliders,
  MapPin,
  Clock,
  AlertTriangle,
  QrCode
} from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { ProductModal, StockAdjustmentModal } from "./ProductModal";

export function InventoryModule() {
  const { products, batches, warehouses, deleteProduct } = useApp();
  const [searchParams] = useSearchParams();
  const searchParamVal = searchParams.get("search") || "";

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (searchParamVal && products.length > 0) {
      const matched = products.find((p) => p.name.toLowerCase().includes(searchParamVal.toLowerCase()));
      if (matched) {
        setExpandedRows((prev) => ({ ...prev, [matched.id]: true }));
      }
    }
  }, [searchParamVal, products]);

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const columns = [
    {
      header: "Product & SKU",
      accessorKey: "name",
      render: (val, row) => {
        const isExpanded = expandedRows[row.id];
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpand(row.id);
              }}
              className="p-1 text-slate-400 hover:text-slate-900 dark:hover:text-white"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <div>
              <span className="font-bold text-slate-900 dark:text-white text-xs">{val}</span>
              <p className="text-[10px] text-slate-400 font-mono">
                SKU: {row.id} • Barcode: {row.barcode}
              </p>
            </div>
          </div>
        );
      }
    },
    {
      header: "Category",
      accessorKey: "category",
      render: (val) => <Badge variant="default">{val}</Badge>
    },
    {
      header: "Cost / Retail / MRP",
      accessorKey: "purchasePrice",
      align: "right",
      render: (_, row) => (
        <div className="text-right">
          <span className="font-mono text-slate-800 dark:text-slate-200 font-semibold">
            {formatCurrency(row.retailPrice)}
          </span>
          <p className="text-[10px] text-slate-400 font-mono">
            Cost: ₹{row.purchasePrice} | MRP: ₹{row.MRP}
          </p>
        </div>
      )
    },
    {
      header: "Total Stock",
      accessorKey: "id",
      render: (id, row) => {
        const prodBatches = batches.filter((b) => b.productId === id && b.status !== "Expired");
        const total = prodBatches.reduce((sum, b) => sum + (Number(b.qtyRemaining) || 0), 0);
        const isLow = total <= row.reorderLevel;

        return (
          <div className="flex items-center gap-2">
            <span
              className={`font-mono font-bold text-xs ${
                isLow ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"
              }`}
            >
              {total} {row.unit}s
            </span>
            {isLow && (
              <span className="bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded text-[10px] font-bold">
                Low Stock
              </span>
            )}
          </div>
        );
      }
    },
    {
      header: "Reorder Lvl",
      accessorKey: "reorderLevel",
      align: "center",
      render: (val) => <span className="font-mono text-slate-500">{val}</span>
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setProductToEdit(row);
              setIsProductModalOpen(true);
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Edit SKU Details"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete product ${row.name} from catalog?`)) {
                deleteProduct(row.id);
              }
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
            title="Delete SKU"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Boxes className="w-5 h-5 text-emerald-500" />
            Master Product Inventory & Stock Ledger
          </h2>
          <p className="text-xs text-slate-500">
            7-state stock ledger (Available, Reserved, Damaged, Returned, Expired, In-Transit) broken down by warehouse & batch
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setIsAdjustmentModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl transition"
          >
            <Sliders className="w-4 h-4" />
            Stock Adjustment / Audit
          </button>

          <button
            onClick={() => {
              setProductToEdit(null);
              setIsProductModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
          >
            <Plus className="w-4 h-4" />
            Add Master Product SKU
          </button>
        </div>
      </div>

      {/* Main Table with Expandable 7-State Sub-rows */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={products}
          title="Product Master Catalog"
          subtitle="Click the arrow on any product to reveal exact batch breakdowns across warehouses"
          filename="kirana_inventory_catalog"
        />

        {/* Render Expanded Batch Details for each opened row */}
        {Object.entries(expandedRows).map(([prodId, isExpanded]) => {
          if (!isExpanded) return null;
          const product = products.find((p) => p.id === prodId);
          const prodBatches = batches.filter((b) => b.productId === prodId);

          return (
            <div
              key={prodId}
              className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-emerald-500/40 shadow-sm space-y-3 animate-in fade-in duration-200 text-xs"
            >
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  <h4 className="font-bold text-slate-900 dark:text-white">
                    Warehouse & Batch Breakdown: {product?.name}
                  </h4>
                </div>
                <span className="font-mono text-slate-500 text-[11px]">
                  {prodBatches.length} Active Storage Lots
                </span>
              </div>

              {prodBatches.length === 0 ? (
                <p className="text-slate-400 italic">No batches currently stored in any warehouse.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="text-[10px] text-slate-500 uppercase border-b border-slate-200 dark:border-slate-800 font-semibold">
                      <tr>
                        <th className="py-2">Warehouse</th>
                        <th className="py-2">Exact Bin</th>
                        <th className="py-2">Batch No</th>
                        <th className="py-2">Mfg Date</th>
                        <th className="py-2">Expiry Date</th>
                        <th className="py-2 text-right">Available Qty</th>
                        <th className="py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200/60 dark:divide-slate-800/60">
                      {prodBatches.map((b) => {
                        const wh = warehouses.find((w) => w.id === b.warehouseId);
                        return (
                          <tr key={b.id}>
                            <td className="py-2 font-medium text-slate-800 dark:text-slate-200">
                              {wh?.name || b.warehouseCode}
                            </td>
                            <td className="py-2 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                              {b.binCode}
                            </td>
                            <td className="py-2 font-mono text-slate-700 dark:text-slate-300">
                              {b.batchNo}
                            </td>
                            <td className="py-2 text-slate-500">{formatDate(b.mfgDate)}</td>
                            <td className="py-2 text-slate-500">{formatDate(b.expiryDate)}</td>
                            <td className="py-2 text-right font-mono font-bold text-slate-900 dark:text-white">
                              {b.qtyRemaining} {product?.unit}s
                            </td>
                            <td className="py-2 text-center">
                              <Badge
                                variant={
                                  b.status === "Expired"
                                    ? "rose"
                                    : b.status === "Expiring Soon"
                                    ? "amber"
                                    : "emerald"
                                }
                              >
                                {b.status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        productToEdit={productToEdit}
      />

      <StockAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onClose={() => setIsAdjustmentModalOpen(false)}
      />
    </div>
  );
}
